import {
  ChakraBox,
  ChakraCollapse,
  ChakraFlex,
  FAIconName,
  Icon,
  TagDropdown,
  TagProps,
  Text,
} from '@equip.health/ui';
import { CarePlanQolGoalResponse } from '~/lib/types/carePlan/carePlans';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import { buildGoalStatusTagDropdownProps } from './CarePlanGoalTagDropdownOptions';
import { CarePlanGoalCollapsedView } from './CarePlanGoalCollapsedView';
import { CarePlanQoLGoalExpandedView } from './CarePlanQoLGoalExpandedView';
import { CarePlanGoalEditMenu } from './CarePlanGoalEditMenu';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';

type Props = {
  qolGoal: CarePlanQolGoalResponse;
  isExpanded: boolean;
  carePlanId: string;
  statusDropdownOptions: TagProps[];
  onToggle: (goalId: string) => void;
  onAddDescriptionClick: (qolGoal: CarePlanQolGoalResponse) => void;
  onEditQoLGoalClick: (qolGoal: CarePlanQolGoalResponse) => void;
  onRemoveQoLGoalClick: (qolGoalId: string) => void;
  onStatusChange: (
    goalId: string,
    statusCode: CarePlanGoalStatus,
    description: string,
  ) => void;
};

export const CarePlanQoLGoalRow = ({
  qolGoal,
  isExpanded,
  carePlanId,
  statusDropdownOptions,
  onToggle,
  onAddDescriptionClick,
  onEditQoLGoalClick,
  onRemoveQoLGoalClick,
  onStatusChange,
}: Props) => {
  const { trackEvent } = useAnalytics();

  const handleToggleClick = () => {
    if (isExpanded) {
      trackEvent(CarePlanEvents.clickOnCollapseQolGoalChevron);
    } else {
      trackEvent(CarePlanEvents.clickOnExpandQolGoalChevron);
    }
    onToggle(qolGoal.id);
  };

  return (
    <ChakraBox
      borderWidth="1px"
      borderColor="neutral.line"
      borderRadius="12px"
      marginBottom="12px"
      bg="white"
    >
      <ChakraBox
        as="button"
        paddingTop="18px"
        paddingLeft="18px"
        paddingRight="0px"
        paddingBottom="12px"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        onClick={handleToggleClick}
        _hover={{ bg: 'neutral.background' }}
        width="100%"
        textAlign="left"
      >
        <ChakraFlex alignItems="center" gap="8px" flex="1">
          <Icon
            aria-label={`Expand or collapse ${qolGoal.title}`}
            color="neutral.primary"
            name={
              isExpanded
                ? FAIconName.chevronUpRegular
                : FAIconName.chevronDownRegular
            }
          />
          <Text variant="bodyMedium">{qolGoal.title}</Text>
        </ChakraFlex>

        <ChakraFlex
          alignItems="center"
          gap="12px"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <TagDropdown
            id={`qol-goal-${carePlanId}-${qolGoal.id}-status-dropdown`}
            menuOptions={statusDropdownOptions}
            onOptionClick={(newStatus) => {
              onStatusChange(
                qolGoal.id,
                newStatus as CarePlanGoalStatus,
                qolGoal.description || '',
              );
            }}
            {...buildGoalStatusTagDropdownProps(
              qolGoal.status.code,
              qolGoal.status.label,
              statusDropdownOptions,
            )}
          />

          <CarePlanGoalEditMenu
            goalTitle={qolGoal.title}
            onEditClick={() => onEditQoLGoalClick(qolGoal)}
            onRemoveClick={() => onRemoveQoLGoalClick(qolGoal.id)}
          />
        </ChakraFlex>
      </ChakraBox>

      <ChakraBox
        id={`care-plan-qol-goal-${qolGoal.id}`}
        paddingLeft="42px"
        paddingRight="18px"
      >
        {!isExpanded &&
          (qolGoal.descriptionDetails?.description?.trim() ? (
            <CarePlanGoalCollapsedView
              noteText={qolGoal.descriptionDetails.description}
              timestamp={qolGoal.descriptionDetails.effectiveDate}
              providerId={qolGoal.descriptionDetails.changedBy}
              goalId={qolGoal.id}
            />
          ) : (
            qolGoal.description?.trim() && (
              <CarePlanGoalCollapsedView
                noteText={qolGoal.description}
                timestamp={qolGoal.createdAt}
                providerId={qolGoal.createdByUserId}
                goalId={qolGoal.id}
              />
            )
          ))}
        <ChakraCollapse in={isExpanded} animateOpacity>
          <CarePlanQoLGoalExpandedView
            qolGoal={qolGoal}
            onAddDescriptionClick={onAddDescriptionClick}
          />
        </ChakraCollapse>
      </ChakraBox>
    </ChakraBox>
  );
};
